import {
  ChatChartSeriesDto,
  ChatChartSpecDto,
  ChatTableSpecDto,
} from 'src/api/FiretrolApiClient';
import { chartValueAt } from 'src/app/chat/chatChartFormat';
import { formatDate } from 'src/utils';

export type CsvCellValue = string | number | null | undefined;

export const CSV_MIME_TYPE = 'text/csv;charset=utf-8';

const BOM = '\uFEFF';
const LINE_END = '\r\n';
const FORMULA_TRIGGERS = new Set(['=', '+', '-', '@', '\t', '\r']);
const QUOTE_TRIGGER = /[",\r\n]/;

const quote = (value: string): string =>
  QUOTE_TRIGGER.test(value) ? `"${value.replace(/"/g, '""')}"` : value;

// A spreadsheet evaluates a text cell that starts with a formula character, so
// such a cell gets a leading ' to force it to text. A finite number cannot be a
// formula and stays unguarded so a negative value is still a number.
const formatCell = (value: CsvCellValue): string => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return String(value);
  }
  const text = value === null || value === undefined ? '' : String(value);
  if (!text) return '';
  return quote(FORMULA_TRIGGERS.has(text[0]) ? `'${text}` : text);
};

/** RFC 4180 CSV with a UTF-8 byte order mark and CRLF line ends, as the API's CSV exports write it. */
export const buildCsv = (
  header: readonly string[],
  rows: readonly (readonly CsvCellValue[])[],
): string =>
  BOM +
  [header, ...rows]
    .map((row) => row.map(formatCell).join(',') + LINE_END)
    .join('');

// The raw numbers lose the table's $, % and h formatting, so the unit moves
// into the column header.
const UNIT_HEADER_SUFFIX: Record<string, string> = {
  currency: 'USD',
  percent: '%',
  hours: 'hours',
};

const csvHeaderWithUnit = (label: string, unit: string): string => {
  const suffix = UNIT_HEADER_SUFFIX[unit];
  return suffix ? `${label} (${suffix})` : label;
};

export const chartSeriesCsvHeader = (series: ChatChartSeriesDto): string =>
  csvHeaderWithUnit(series.label, series.unit);

/** The columns and rows of the chart's Show data table, with raw numbers instead of formatted values. */
export const buildChartCsv = (spec: ChatChartSpecDto): string =>
  buildCsv(
    [spec.xLabel, ...spec.series.map(chartSeriesCsvHeader)],
    spec.points.map((point) => [
      point.x,
      ...spec.series.map((_, index) => chartValueAt(point, index)),
    ]),
  );

const tableCsvCell = (value: unknown): CsvCellValue => {
  if (typeof value === 'boolean') return value ? 'true' : 'false';
  if (typeof value === 'number' || typeof value === 'string') return value;
  return null;
};

/** A table spec's columns with the given rows (the shown rows, in display order), with raw values instead of formatted ones. */
export const buildTableCsv = (
  spec: ChatTableSpecDto,
  rows: readonly (readonly unknown[])[],
): string =>
  buildCsv(
    spec.columns.map((column) =>
      csvHeaderWithUnit(column.label, column.format),
    ),
    rows.map((row) => spec.columns.map((_, index) => tableCsvCell(row[index]))),
  );

/** `fire-{kind}-YYYY-MM-DD-HHmmss.csv` in local time. */
export const chatCsvFileName = (
  kind: 'query' | 'chart' | 'table',
  exportedAt: Date,
): string => `fire-${kind}-${formatDate(exportedAt, 'YYYY-MM-DD-HHmmss')}.csv`;
